import { useEffect, useLayoutEffect, useState, useCallback, useRef, MouseEvent, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { EditorToolbar } from "./EditorToolbar";
import { FlagDeficiencyDialog } from "@/zapi-frontend/features/FlagDeficiency";
import { SelectionImagePrompt } from "./_SelectionImagePrompt";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { ChatCircleTextIcon, FlagIcon, ChartBarIcon, PaperPlaneRightIcon } from "@phosphor-icons/react";
import { CHART_BLOCK_NAME } from "../extensions/ChartBlock";
import { ProseWrapper, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useTranslation } from "react-i18next";
import { RecropModal } from "@/zapi-frontend/widgets/ImageCropper";
import { createEditorExtensions } from "../utils/editor-extensions";
import { createEditorProps } from "../utils/editor-props";
import { createAutoSaveHandler } from "../utils/content-management";
import {
  preserveEmptyParagraphs,
  patchParagraphSerializer,
  tightenListSpacing,
  memoContentEquivalent
} from "../utils/preserve-empty-paragraphs";
import { useCropManagement } from "../hooks/use-crop-management";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useYdocProvider } from "@/zapi-frontend/shared/hooks/useYdocProvider";
import Collaboration from "@tiptap/extension-collaboration";
import { CollaborationCursor } from "@tiptap/extension-collaboration-cursor";
import { CellSelection } from "@tiptap/pm/tables";
import { randomColor } from "@/zapi-frontend/utils/colors";
import { DecorationAttrs } from "@/zapi-frontend/widgets/TipTapEditor/model/types";
import { useChartGenerationStore } from "@/zapi-frontend/widgets/TipTapEditor/model/chartGenerationStore";
import { useCommentStore } from "@/zapi-frontend/widgets/CommentsIsland/model/store/commentsStore";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { removeAllCommentMarks, removePersistentCommentMark } from "../utils/removeCommentMark";
import { applySelectionHighlight, clearSelectionHighlight } from "../utils/selectionHighlight";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";
import { getSizeBytes } from "../utils/getSizeBytes";
import { MEMO_BYTE_LIMIT } from "../const/const";
import { nanoid } from "nanoid";
import { STUB_COMMENT_ID } from "../../CommentsIsland/const/const";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { scrollIntoView } from "@/zapi-frontend/shared/lib/scrollIntoView";
import { highlightTextInEditor, highlightTableRowInEditor, scrollToPos } from "../lib/editorHighlighting";
import { autoSetTableColumnWidths, TABLE_LAYOUT_META } from "../utils/auto-table-widths";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { RegenerateButton } from "./_RegenerateButton";

const processedEvents = new Map();

type TTipTapEditorProps = {
  projectId: string;
  content?: string;
  fileName?: string;
  editable?: boolean;
  autoSave?: boolean;
};

export default function TipTapEditor({
  projectId,
  content: _content,
  fileName: fileNameFromProps = "memo.md",
  editable = true,
  autoSave = false
}: TTipTapEditorProps) {
  const { t } = useTranslation();
  const fileName = fileNameFromProps;

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSaveRef = useRef<((force: boolean) => Promise<void> | void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [syncInProgress, setSyncInProgress] = useState(false);

  const { selectedProject } = useProjectStore();
  const chatEnabled = selectedProject?.chatEnabled;

  const { activeChapter, updateChapterContent, syncChapterContent, invalidateChapterCroppedImages } = useChapterStore();

  const {
    setSetContent,
    setCommentsUpdated,
    setImgsCropUpdated,
    setGetMarkdown,
    setGetHtml,
    setPasteContent,
    setHighlightText,
    setHighlightTableRow,
    setRemoveCommentMark,
    setFlushSave
  } = useEditorStore();
  const { provider, ydoc, userName, iAmLeader, loading: isConnectingToYdcoProvider } = useYdocProvider(projectId);
  const { createComment, fetchComments } = useCommentStore();
  const { runTimeout } = useScopedTimeout();
  const { openHandler, scrollToBottomCallback } = useProjectSidebarStore();
  const isLaptop = useMediaQuery("(min-width: 984px)");
  const isSynced = !isConnectingToYdcoProvider && !syncInProgress;

  // For temp comment
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const limitExceededRef = useRef(false); // for access inside editor‑props
  limitExceededRef.current = isLimitExceeded;
  const [showCommentButton, setShowCommentButton] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [selection, setSelection] = useState({ from: 0, to: 0 });
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFlagDialogOpen, setIsFlagDialogOpen] = useState(false);
  const [flagQuote, setFlagQuote] = useState("");
  const commentInputRef = useRef<HTMLInputElement>(null);
  const selectionToolbarRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const toolbar = selectionToolbarRef.current;
    if (!toolbar) return;
    const containerRect = containerRef.current?.getBoundingClientRect();
    const minLeft = (containerRect?.left ?? 0) + 8;
    const maxLeft = (containerRect?.right ?? window.innerWidth) - toolbar.offsetWidth - 8;
    toolbar.style.left = `${Math.max(minLeft, Math.min(buttonPosition.left - 70, maxLeft))}px`;
  }, [buttonPosition, showCommentButton, showCommentInput, isFlagDialogOpen]);

  const [chapterPromptOpen, setChapterPromptOpen] = useState(false);
  const [chapterPromptValue, setChapterPromptValue] = useState("");
  const [chapterPromptError, setChapterPromptError] = useState<string | null>(null);
  const [chapterPromptAnchor, setChapterPromptAnchor] = useState<{ top: number; left: number } | null>(null);
  const chapterPromptContextRef = useRef<{ chapterText: string; insertPos: number } | null>(null);

  const handleCommentClick = () => {
    if (editor) {
      const wasEditable = editor.isEditable;
      if (!wasEditable) editor.setEditable(true);
      editor.commands.setMark("commentMark", {});
      const { from } = editor.state.selection;
      editor.commands.setTextSelection(from);
      if (!wasEditable) editor.setEditable(false);
    }
    setShowCommentInput(true);
    setShowCommentButton(false);
  };

  const updateChartNodeById = (chartId: string, attrs: Record<string, unknown>) => {
    if (!editor) return;
    let pos: number | null = null;
    editor.state.doc.descendants((n, p) => {
      if (n.type.name === CHART_BLOCK_NAME && n.attrs.chartId === chartId) {
        pos = p;
        return false;
      }
      return true;
    });
    if (pos === null) return;
    const target = pos as number;
    editor
      .chain()
      .command(({ tr }) => {
        const found = tr.doc.nodeAt(target);
        if (!found) return false;
        tr.setNodeMarkup(target, undefined, { ...found.attrs, ...attrs });
        return true;
      })
      .run();
  };

  const scrollToChartNodeById = (chartId: string) => {
    if (!editor) return;
    let pos: number | null = null;
    editor.state.doc.descendants((n, p) => {
      if (n.type.name === CHART_BLOCK_NAME && n.attrs.chartId === chartId) {
        pos = p;
        return false;
      }
      return true;
    });
    if (pos === null) return;
    const target = pos as number;
    requestAnimationFrame(() => scrollToPos(editor, target));
  };

  const generateChartAt = async (srcText: string, insertPos: number, userPrompt?: string) => {
    if (!editor || !srcText.trim()) return;
    const chartId = nanoid();
    const promptText = userPrompt?.trim() ?? "";

    editor
      .chain()
      .insertContentAt(insertPos, {
        type: CHART_BLOCK_NAME,
        attrs: {
          chartId,
          title: "",
          imageUrl: "",
          sourceText: srcText,
          prompt: promptText,
          status: "default"
        }
      })
      .run();

    clearSelectionHighlight(editor);
    scrollToChartNodeById(chartId);

    // Persist the placeholder to this chapter's memo right away (while it is still
    // the active chapter) rather than waiting for the 250ms debounce. Otherwise, if
    // the user navigates to another chapter before the debounce fires, the node is
    // never saved and the finished image has no placeholder to attach to on return.
    if (autoSave && projectId) {
      void handleAutoSave(editor.storage.markdown.getMarkdown());
    }

    // Generation is tracked in a module-level store so it survives the editor
    // unmounting (e.g. switching to the presentation tab). The store is the source
    // of truth for the outcome; reconcileChartPlaceholders applies it to whichever
    // editor instance is mounted for the chapter — now or after a remount.
    const chartStore = useChartGenerationStore.getState();
    const chapterKey = activeChapter?.id?.toString() ?? "";
    chartStore.start(chartId, chapterKey);
    const editorIsMounted = () => !!editorRef.current && !editorRef.current.isDestroyed;
    try {
      const payload: { selectedText: string; prompt?: string; chartId: string } = {
        selectedText: srcText,
        chartId
      };
      if (promptText) payload.prompt = promptText;
      const res = await $api.post<{ data: { visualize: boolean; imageUrl: string; label: string } }>(
        `/projects/${projectId}/images/generate`,
        payload
      );
      if (!res.data.data.visualize) {
        chartStore.markEmpty(chartId);
        if (editorIsMounted()) toast("There's nothing to visualize in this text.");
        return;
      }
      chartStore.succeed(chartId, { imageUrl: res.data.data.imageUrl, title: res.data.data.label });
    } catch (error) {
      console.error("Failed to generate chart:", error);
      chartStore.fail(chartId);
      if (editorIsMounted()) toast.error("Something went wrong generating the chart. Please try again.");
    }
  };

  // Reconcile chart placeholder nodes (empty imageUrl) against the generation store:
  // apply completed images and drop the ones with no visualization/errors. A
  // placeholder with no store entry is left loading — it may still be generating in
  // another client or editor instance (the store is per-context, but the doc is
  // shared collaboratively), and its result will arrive when that generation
  // finishes. Removing it here would abort a chart that is generating elsewhere.
  const reconcileChartPlaceholders = () => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed) return;
    const chartStore = useChartGenerationStore.getState();
    const placeholders: { chartId: string; from: number; to: number }[] = [];
    editor.state.doc.descendants((n, p) => {
      if (n.type.name === CHART_BLOCK_NAME && !n.attrs.imageUrl) {
        placeholders.push({ chartId: n.attrs.chartId, from: p, to: p + n.nodeSize });
      }
      return true;
    });

    const removals: { from: number; to: number }[] = [];
    for (const placeholder of placeholders) {
      const entry = chartStore.generations[placeholder.chartId];
      if (!entry || entry.status === "pending") continue;
      if (entry.status === "done" && entry.result) {
        updateChartNodeById(placeholder.chartId, {
          imageUrl: entry.result.imageUrl,
          title: entry.result.title,
          status: "default"
        });
        // Keep the done entry for the rest of the session. On chapter re-entry the
        // leader re-seeds the DB markdown (a placeholder — the image was never saved
        // to the DB while this chapter was unmounted) over the just-resolved node; if
        // the entry were cleared here, that re-created placeholder would flash
        // "Creating chart..." until the node poll. Retaining it lets ChartBlockView
        // resolve instantly from the store and lets this reconcile re-stamp the node.
      } else {
        removals.push({ from: placeholder.from, to: placeholder.to });
        chartStore.clear(placeholder.chartId);
      }
    }

    if (removals.length) {
      let chain = editor.chain();
      // Delete from last to first so earlier positions stay valid within the chain.
      for (const range of removals.sort((a, b) => b.from - a.from)) {
        chain = chain.deleteRange(range);
      }
      chain.run();
    }
  };

  // Snapshot chart nodes that already have a resolved image. A generation that
  // completes while its chapter is unmounted is never written into that chapter's
  // server memo (only into Redis + the in-memory store), so the server copy stays a
  // placeholder. On chapter re-entry setContent runs with both the image-carrying
  // cache and the placeholder server copy; without this, the placeholder version
  // regresses the node from image back to the "Creating chart..." loader (which then
  // refills ~2s later via the node-level poll — a visible image→loader→image flicker).
  const captureResolvedCharts = () => {
    const editor = editorRef.current;
    const resolved = new Map<string, { imageUrl: string; title: string; status: string }>();
    if (!editor || editor.isDestroyed) return resolved;
    editor.state.doc.descendants((n) => {
      if (n.type.name === CHART_BLOCK_NAME && n.attrs.imageUrl) {
        resolved.set(n.attrs.chartId, {
          imageUrl: n.attrs.imageUrl,
          title: n.attrs.title,
          status: n.attrs.status
        });
      }
      return true;
    });
    return resolved;
  };

  // Replace the editor content while preserving any locally-resolved chart images
  // against a stale placeholder in the incoming markdown. Restore happens in the same
  // synchronous tick as setContent, so the chart node view never paints a loader frame,
  // and the resulting transaction persists the image through the normal autosave path.
  const setContentPreservingResolvedCharts = (markdown: string) => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed) return;
    const resolved = captureResolvedCharts();
    editor.commands.setContent(preserveEmptyParagraphs(markdown));
    autoSetTableColumnWidths(editor);
    if (resolved.size) {
      const restorations: { chartId: string; attrs: { imageUrl: string; title: string; status: string } }[] = [];
      editor.state.doc.descendants((n) => {
        if (n.type.name === CHART_BLOCK_NAME && !n.attrs.imageUrl) {
          const hit = resolved.get(n.attrs.chartId);
          if (hit) restorations.push({ chartId: n.attrs.chartId, attrs: hit });
        }
        return true;
      });
      for (const restoration of restorations) updateChartNodeById(restoration.chartId, restoration.attrs);
    }
    reconcileChartPlaceholders();
  };

  const handleImageButtonClick = () => {
    if (!editor) return;
    const { from, to } = selection;
    const text = from !== to ? editor.state.doc.textBetween(from, to, " ").trim() : "";
    if (!text) return;
    applySelectionHighlight(editor, from, to);
    setShowCommentButton(false);

    let insertPos = to;
    try {
      insertPos = editor.state.doc.resolve(to).after(1);
    } catch {
      insertPos = to;
    }
    void generateChartAt(text, insertPos);
  };

  const handleToolbarChartClick = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    const selectedText = hasSelection ? editor.state.doc.textBetween(from, to, " ").trim() : "";

    if (hasSelection && selectedText) {
      applySelectionHighlight(editor, from, to);
      setShowCommentButton(false);
      let insertPos = to;
      try {
        insertPos = editor.state.doc.resolve(to).after(1);
      } catch {
        insertPos = to;
      }
      void generateChartAt(selectedText, insertPos);
      return;
    }

    const chapterText = editor.storage.markdown?.getMarkdown?.()?.trim() || editor.state.doc.textContent.trim();
    if (!chapterText) {
      toast.error("Chapter is empty — add some content before creating a chart.");
      return;
    }
    const cursorPos = from;
    let insertPos = cursorPos;
    try {
      insertPos = editor.state.doc.resolve(cursorPos).after(1);
    } catch {
      insertPos = cursorPos;
    }

    let anchor: { top: number; left: number } | null = null;
    try {
      const coords = editor.view.coordsAtPos(cursorPos);
      anchor = { top: coords.bottom + 8, left: coords.left };
    } catch {
      anchor = null;
    }

    chapterPromptContextRef.current = { chapterText, insertPos };
    setChapterPromptValue("");
    setChapterPromptError(null);
    setChapterPromptAnchor(anchor);
    setChapterPromptOpen(true);
  };

  const handleCancelChapterPrompt = () => {
    setChapterPromptOpen(false);
    setChapterPromptValue("");
    setChapterPromptError(null);
    chapterPromptContextRef.current = null;
  };

  const handleSubmitChapterPrompt = () => {
    const ctx = chapterPromptContextRef.current;
    if (!ctx) return;
    const promptText = chapterPromptValue.trim();
    if (!promptText) return;
    setChapterPromptOpen(false);
    chapterPromptContextRef.current = null;
    void generateChartAt(ctx.chapterText, ctx.insertPos, promptText);
    setChapterPromptValue("");
    setChapterPromptError(null);
  };
  const { firstName, lastName, id: userId } = useMeStore();
  const toolbarInteractionRef = useRef(false);

  const updateLocalContent = useCallback(
    (content: string) => {
      updateChapterContent(activeChapter?.id + "", content, true);
    },
    [updateChapterContent, activeChapter?.id]
  );

  const handleAutoSave = useMemo(
    () =>
      createAutoSaveHandler(
        autoSave,
        projectId,
        iAmLeader,
        activeChapter?.id,
        activeChapter?.isCurrent,
        activeChapter?.projectTemplateChapter?.slug
      ),
    [
      autoSave,
      projectId,
      iAmLeader,
      activeChapter?.id,
      activeChapter?.isCurrent,
      activeChapter?.projectTemplateChapter?.slug
    ]
  );

  const handleAutoSaveRef = useRef(handleAutoSave);
  useEffect(() => {
    handleAutoSaveRef.current = handleAutoSave;
  }, [handleAutoSave]);

  const debouncedUpdate = useCallback(
    (newContent: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      const wasLeader = iAmLeader();
      const runSave = (force: boolean) => {
        updateLocalContent(newContent);

        if (autoSave && projectId) return handleAutoSave(newContent, force && wasLeader);
      };

      pendingSaveRef.current = runSave;
      debounceTimerRef.current = setTimeout(() => {
        pendingSaveRef.current = null;
        runSave(false);
      }, 250);
    },
    [updateLocalContent, autoSave, projectId, handleAutoSave, iAmLeader]
  );

  const flushPendingSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const runSave = pendingSaveRef.current;
    pendingSaveRef.current = null;
    return runSave?.(true);
  }, []);

  const collabExtensions =
    !!ydoc && !!provider
      ? [
          Collaboration.configure({
            document: ydoc,
            field: "default"
          }),
          CollaborationCursor.configure({
            provider,
            user: { name: userName, color: randomColor(), connectedSince: new Date().getTime() },
            render: (user) => {
              /* wrapper <span data-user-id="…"> … */
              const cursor = document.createElement("span");
              cursor.classList.add("z-cursor");
              cursor.setAttribute("data-userName", user.name);
              cursor.style.setProperty("--label-bg", user.color);

              /* the blinking caret ▌ */
              const caret = document.createElement("span");
              caret.classList.add("z-caret");
              caret.style.backgroundColor = user.color;

              /* the name bubble */
              const label = document.createElement("span");
              label.classList.add("z-label");
              label.style.backgroundColor = user.color;
              label.textContent = user.name;

              cursor.appendChild(caret);
              cursor.appendChild(label);
              return cursor;
            },
            selectionRender: (user) => {
              const attr: DecorationAttrs = {
                "data-userName": user.name,
                "data-color": user.color,
                class: "z-selection",
                nodeName: "span",
                style: `background-color: ${user.color}40; --label-bg:${user.color};`
              };
              return attr;
            }
          })
        ]
      : [];

  const editor = useEditor(
    {
      immediatelyRender: true,
      extensions: createEditorExtensions(collabExtensions),
      onUpdate: ({ editor, transaction }) => {
        if (isConnectingToYdcoProvider || !transaction.docChanged) return;
        if (transaction.getMeta(TABLE_LAYOUT_META)) return;

        const isOnlyCommentMarkChange = transaction.steps.every((step) => {
          const stepJson = step.toJSON();
          return (
            stepJson.stepType === "addMark" &&
            (stepJson.mark?.type === "persistentCommentMark" || stepJson.mark?.type === "commentMark")
          );
        });

        if (isOnlyCommentMarkChange) return;

        try {
          const markdown = tightenListSpacing(editor.storage.markdown.getMarkdown());
          const tooBig = getSizeBytes(markdown) > MEMO_BYTE_LIMIT;
          setIsLimitExceeded(tooBig);

          debouncedUpdate(markdown);
        } catch (error) {
          // Silently handle markdown parsing errors
        }
      },
      onFocus: () => {
        setIsEditorFocused(true);
      },
      onBlur: () => {
        setTimeout(() => {
          if (toolbarInteractionRef.current) return;

          const activeElement = document.activeElement;
          const toolbarElements = document.querySelectorAll('[data-toolbar="true"]');
          let isToolbarFocused = false;

          toolbarElements.forEach((toolbar) => {
            if (toolbar.contains(activeElement) || toolbar === activeElement) {
              isToolbarFocused = true;
            }
          });

          const isInToolbar = activeElement?.closest('[data-toolbar="true"]') !== null;

          if (!isToolbarFocused && !isInToolbar) {
            setIsEditorFocused(false);
          }
        }, 150);
      },
      editable: editable && isSynced
    },
    [ydoc, provider, debouncedUpdate, isConnectingToYdcoProvider, activeChapter?.id]
  );

  // WARN we need a ref to consistently use latest editor instance, sometimes it can change and render editor usage on closures stale.
  // useLayoutEffect (runs before every useEffect) so effects that apply content via
  // editorRef never see the previous, destroyed instance on the render where the
  // editor is recreated — setContentPreservingResolvedCharts would bail silently
  // on isDestroyed and leave the chapter blank.
  const editorRef = useRef(editor);
  useLayoutEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable && isSynced);
    }
  }, [editor, editable, isSynced]);

  useEffect(() => {
    if (editor) {
      patchParagraphSerializer(editor);
    }
  }, [editor]);

  // TODO - do something better than this
  useEffect(() => {
    if (activeChapter?.activeMemo?.mdContent && editor && isSynced) {
      const currentContent = editor.storage.markdown.getMarkdown();
      if (!memoContentEquivalent(currentContent, activeChapter.activeMemo.mdContent)) {
        setContentPreservingResolvedCharts(activeChapter.activeMemo.mdContent);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChapter?.activeMemo?.mdContent, editor, isSynced]);

  // On chapter entry the editor's scroll container must start at the top. Safari
  // aligns the first node (a table on the exec summary) to the top of the scroll
  // box even when it's already fully visible, scrolling the `pt-10` padding and the
  // exec-summary header out of view — so we explicitly pin scrollTop to 0 once the
  // content has settled. Skipped when a subsection deep-link owns the scroll target.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isSynced) return;
    if (new URLSearchParams(window.location.search).has("chapterSubsection")) return;

    const resetToTop = () => {
      container.scrollTop = 0;
    };
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(resetToTop);
    });
    runTimeout(resetToTop, 250);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChapter?.id, isSynced]);

  useEffect(() => {
    if (isLaptop) return;

    const handleToolbarTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-toolbar="true"]')) {
        toolbarInteractionRef.current = true;
      }
    };

    const handleToolbarTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-toolbar="true"]')) {
        toolbarInteractionRef.current = true;
        setTimeout(() => {
          toolbarInteractionRef.current = false;
        }, 500);
      }
    };

    document.addEventListener("touchstart", handleToolbarTouchStart, { passive: true });
    document.addEventListener("touchend", handleToolbarTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleToolbarTouchStart);
      document.removeEventListener("touchend", handleToolbarTouchEnd);
    };
  }, [isLaptop]);

  const chartGenerations = useChartGenerationStore((state) => state.generations);
  useEffect(() => {
    reconcileChartPlaceholders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartGenerations, editor, isSynced]);

  // Chart results live in the module store and are applied by reconcile. When a
  // chapter is re-entered, its placeholder nodes re-hydrate from Yjs across one or
  // more transactions AFTER the effects above have already run — and the store
  // entries no longer change, so nothing re-triggers reconcile. Reconcile on every
  // editor update so a completed image is applied as soon as its placeholder lands.
  // A scan with no matching store entry is a no-op, so this cannot loop.
  useEffect(() => {
    if (!editor) return;
    const run = () => reconcileChartPlaceholders();
    editor.on("update", run);
    return () => {
      editor.off("update", run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const {
    recropModalOpen,
    recropImage,
    handleImageClick,
    handleRecropSuccess,
    applyCropDataToImages,
    setRecropModalOpen
  } = useCropManagement(editor);

  useEffect(() => {
    if (editor) {
      const editorProps = createEditorProps({
        editor,
        projectId,
        fileName,
        onImageClick: handleImageClick,
        limitReached: () => limitExceededRef.current,
        onLimitExceeded: () =>
          reportErrorToSentry(
            `Memo size limit exceeded`,
            `Project:${selectedProject?.name}; Chapter: ${activeChapter?.projectTemplateChapter.name}`,
            "",
            { projectId, chapterId: activeChapter?.id }
          )
      });

      Object.assign(editor.view.props, editorProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, projectId, fileName, handleImageClick, isLimitExceeded]);

  // This function and following effect does two very important things
  // 1. It sets the initial content of the editor when it mounts (keeping track of initialized state).
  // 2. It only initializes memo content after loading is complete, this means that Yjs document and provider are completely synced and no memo duplication will happen.
  const resolveMemoContent = (
    content = "",
    callbacks: { onResolved?: () => void; onError?: () => void; onFinish?: () => void }
  ) => {
    const editor = editorRef.current;
    const { onError, onFinish, onResolved } = callbacks;

    if (!editor || isConnectingToYdcoProvider || !iAmLeader()) {
      onFinish?.();
      return;
    }

    try {
      const currentContent = editor.storage.markdown.getMarkdown();
      if (content && !memoContentEquivalent(currentContent, content)) {
        setContentPreservingResolvedCharts(content);
        const size = getSizeBytes(content);
        const tooBig = size > MEMO_BYTE_LIMIT;
        setIsLimitExceeded(tooBig);
        onResolved?.();
      }
      onFinish?.();
    } catch (error) {
      onError?.();
      onFinish?.();
    }
  };
  const contentSyncAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    const chapterId = activeChapter?.id;

    if (projectId && chapterId) {
      setSyncInProgress(true);
      if (contentSyncAbortController.current) contentSyncAbortController.current.abort();
      contentSyncAbortController.current = new AbortController();

      syncChapterContent(chapterId.toString(), contentSyncAbortController.current)
        .then((updatedMemoContent) => {
          if (updatedMemoContent !== null)
            return resolveMemoContent(updatedMemoContent, {
              onFinish: () => {
                setSyncInProgress(false);
              },
              onResolved: () => {
                // placeholder
              }
            });
        })
        .finally(() => {
          setSyncInProgress(false);
        });
    }

    return () => {
      contentSyncAbortController.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, activeChapter?.id]);

  const commentsUpdated = useCallback(() => {
    provider?.awareness?.setLocalStateField("customEvent", {
      type: "commentChangeEvent",
      payload: { shouldFetch: true, id: nanoid() }
    });
  }, [provider]);

  const imgsCropUpdated = useCallback(() => {
    provider?.awareness?.setLocalStateField("customEvent", {
      type: "imgsCropChangeEvent",
      payload: { shouldFetch: true, id: nanoid() }
    });
  }, [provider]);

  useEffect(() => {
    if (!isConnectingToYdcoProvider && editor) {
      setSetContent((content) => {
        if (document.activeElement === editor.view.dom) {
          return;
        }

        const currentContent = editor.storage.markdown.getMarkdown();
        if (content && !memoContentEquivalent(currentContent, content)) {
          editor.commands.setContent(preserveEmptyParagraphs(content));
          autoSetTableColumnWidths(editor);
          debouncedUpdate(content);
        }
      });
      setCommentsUpdated(commentsUpdated);
      setImgsCropUpdated(imgsCropUpdated);
      setGetMarkdown(() => editor.storage.markdown.getMarkdown());
      setGetHtml(() => editor.getHTML());
      // Explicit paste from the "…" menu: replace content unconditionally (no
      // activeElement guard) and parse markdown so it renders formatted.
      setPasteContent((content) => {
        if (!content) return;
        editor.commands.setContent(preserveEmptyParagraphs(content));
        autoSetTableColumnWidths(editor);
        debouncedUpdate(editor.storage.markdown.getMarkdown() ?? content);
      });
      setHighlightText((text: string) => {
        highlightTextInEditor(editor, text);
      });
      setHighlightTableRow((originalRow: string, cleanText: string) => {
        highlightTableRowInEditor(editor, originalRow, cleanText);
      });
      setRemoveCommentMark((commentId: string) => {
        removePersistentCommentMark(editor, commentId);
      });
      setFlushSave(async () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        pendingSaveRef.current = null;
        const markdown = editorRef.current?.storage.markdown.getMarkdown();
        if (markdown) {
          await handleAutoSaveRef.current(markdown);
        }
      });
    }
    return () => {
      setSetContent(undefined);
      setCommentsUpdated(undefined);
      setImgsCropUpdated(undefined);
      setGetMarkdown(undefined);
      setGetHtml(undefined);
      setPasteContent(undefined);
      setHighlightText(undefined);
      setHighlightTableRow(undefined);
      setRemoveCommentMark(undefined);
      setFlushSave(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isConnectingToYdcoProvider]);

  // Flush (not discard) any pending save when the chapter changes or the
  // editor unmounts, so edits made within the debounce window aren't lost.
  useEffect(() => {
    return () => {
      flushPendingSave();
    };
  }, [activeChapter?.id, flushPendingSave]);

  // Keep tables adaptive to the editor width (narrow/full-width toggle, side panels, window resize).
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;
    let lastWidth = dom.clientWidth;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      const width = dom.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => autoSetTableColumnWidths(editor, true), 150);
    });
    observer.observe(dom);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [editor]);

  const handleContainerClick = useCallback(
    (event: MouseEvent) => {
      if (!editor || !editable) return;

      const editorContentElement = containerRef.current?.querySelector(".ProseMirror");
      if (!editorContentElement) return;

      const containerElement = containerRef.current;
      if (!containerElement) return;

      const clickedInsideContainer = containerElement.contains(event.target as Node);
      const clickedInsideEditor = editorContentElement.contains(event.target as Node);

      const target = event.target as HTMLElement;

      // Don't focus editor if clicking on comment-related elements
      if (
        target.closest(".comment-input") ||
        target.closest(".comment-input-container") ||
        target.closest(".temporaryComment") ||
        target.closest(".toolbar-image-ai-button") ||
        target.closest(".selection-image-prompt")
      ) {
        return;
      }

      if (clickedInsideContainer && !clickedInsideEditor) {
        editor.commands.focus("end");
      }
    },
    [editor, editable]
  );

  useEffect(() => {
    const content = activeChapter?.activeMemo?.mdContent || "";

    if (editor && content && !isConnectingToYdcoProvider) {
      const timeoutId = setTimeout(() => {
        applyCropDataToImages();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [
    editor,
    activeChapter?.activeMemo?.mdContent,
    activeChapter?.activeMemo?.croppedMemoImages,
    applyCropDataToImages,
    isConnectingToYdcoProvider
  ]);

  const ignoreNextClick = useRef(false);

  useEffect(() => {
    const handleMouseUp = (e: globalThis.MouseEvent) => {
      if (!editor) return;

      const mouseTarget = e.target as HTMLElement;
      if (mouseTarget.closest(".toolbar-comment-button") || mouseTarget.closest(".toolbar-image-ai-button")) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((e as any).isToolbar) {
        setShowCommentButton(false);
        return;
      }

      const { from, to } = editor.state.selection;
      const selectedText = from !== to ? editor.state.doc.textBetween(from, to, " ").trim() : "";
      if (!selectedText) {
        setShowCommentButton(false);
        return;
      }

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const target = e.target as HTMLElement;
      if (!containerRef.current?.contains(target)) {
        setShowCommentButton(false);
        return;
      }

      if (
        target.closest(".comment-input") ||
        target.closest(".comment-input-container") ||
        target.closest(".temporaryComment") ||
        target.closest(".toolbar-image-ai-button") ||
        target.closest(".selection-image-prompt") ||
        target.closest("[data-chart-block]")
      ) {
        return;
      }

      if (showCommentButton && selection.from === from && selection.to === to) {
        setShowCommentButton(false);
        return;
      }

      const top = e.clientY;
      const left = e.clientX - 16;

      setSelection({ from, to });
      setButtonPosition({ top, left });
      setShowCommentButton(true);
      setShowCommentInput(false);
      ignoreNextClick.current = true;
    };

    const handleTouchEnd = (e: globalThis.TouchEvent) => {
      const touchTarget = e.target as HTMLElement;
      if (touchTarget?.closest?.(".toolbar-comment-button") || touchTarget?.closest?.(".toolbar-image-ai-button")) {
        return;
      }
      if (touchTarget?.closest?.('[data-toolbar="true"]')) {
        return;
      }
      if (!containerRef.current?.contains(touchTarget)) {
        setShowCommentButton(false);
        return;
      }
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!editor) return;
          const { from, to } = editor.state.selection;
          if (from === to) {
            setShowCommentButton(false);
            return;
          }
          const domSelection = window.getSelection();
          if (!domSelection || domSelection.rangeCount === 0) return;
          const range = domSelection.getRangeAt(0);
          const startNode = range.startContainer;
          const anchorEl = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : (startNode as HTMLElement);
          if (!containerRef.current || !anchorEl || !containerRef.current.contains(anchorEl)) return;
          const rect = range.getBoundingClientRect();
          setSelection({ from, to });
          setButtonPosition({ top: rect.top, left: rect.left + rect.width / 2 });
          setShowCommentButton(true);
          setShowCommentInput(false);
          ignoreNextClick.current = true;
        })
      );
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [editor, showCommentButton, selection]);

  useEffect(() => {
    if (showCommentInput && commentInputRef.current) {
      setTimeout(() => {
        commentInputRef.current?.focus();
      }, 200);
    }
  }, [showCommentInput]);

  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      // We want to do this on next tick after handle mouse up
      setTimeout(() => {
        if (ignoreNextClick.current) {
          ignoreNextClick.current = false;
          return;
        }
        const target = e.target as HTMLElement;
        const isClickOnCommentElements =
          target.closest(".comment-input") ||
          target.closest(".comment-input-container") ||
          target.closest(".toolbar-comment-button");
        const isClickOnMarkedText = target.closest(".temporaryComment");
        if (!isClickOnCommentElements && !isClickOnMarkedText) {
          setShowCommentButton(false);
          setShowCommentInput(false);
          removeAllCommentMarks(editor);
        }
      }, 10);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [editor, showCommentButton, showCommentInput, selection]);

  useEffect(() => {
    if (!chapterPromptOpen) return;
    const handleOutside = (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target.closest(".selection-image-prompt") ||
        target.closest(".toolbar-image-ai-button") ||
        target.closest("[data-toolbar-chart-button]")
      )
        return;
      handleCancelChapterPrompt();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [chapterPromptOpen]);

  // Delegate clicks on persistent comment marks to open sidebar and scroll
  useEffect(() => {
    if (!editor) return;

    const handlePersistentCommentClick = async (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      const markEl = target && target.closest ? target.closest<HTMLElement>(".persistentComment") : null;

      if (markEl) {
        const commentId = markEl.getAttribute("data-persistent-comment-id");
        if (!commentId) return;

        e.preventDefault();
        e.stopPropagation();

        openHandler("comments");

        const chapterId = activeChapter?.id?.toString();
        if (chapterId) await fetchComments(projectId, chapterId);

        const scrollToComment = () => {
          const el = document.getElementById(commentId) as HTMLElement | null;
          if (!el) {
            setTimeout(scrollToComment, 100);
            return;
          }

          scrollIntoView(el, () => {
            el.classList.add("highlighted");
            void el.offsetWidth;

            runTimeout(() => {
              el.classList.remove("highlighted");
            }, 1000);
          });
        };

        runTimeout(scrollToComment, 200);
      }
    };

    document.addEventListener("click", handlePersistentCommentClick);

    return () => {
      document.removeEventListener("click", handlePersistentCommentClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, activeChapter?.id, projectId]);

  // Handle custom collaborative events
  useEffect(() => {
    if (!provider?.awareness) return;
    const localClientId = provider.awareness.clientID;
    // Listen for events from other users
    provider.awareness.on("update", () => {
      const states = provider.awareness?.getStates();
      states?.forEach((state, clientId) => {
        if (clientId === localClientId) return;
        const ev = state.customEvent;
        if (!ev?.payload?.id) return;
        if (processedEvents.has(ev.payload.id)) return;
        processedEvents.set(ev.payload.id, true);
        if (ev.type === "commentChangeEvent" && ev.payload?.shouldFetch) {
          fetchComments(projectId, activeChapter?.id + "");
        } else if (ev.type === "imgsCropChangeEvent" && ev.payload?.shouldFetch) {
          invalidateChapterCroppedImages(activeChapter?.id + "");
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const handleCommentSubmit = useCallback(
    async (commentContent: string) => {
      if (!editor || !activeChapter?.id) return;

      setIsSubmitting(true);
      try {
        // Get the position of the existing commentMark
        let markFrom = 0;
        let markTo = 0;
        editor.state.doc.descendants((node, pos) => {
          if (!node.isText) return;
          const hasMark = node.marks.some((mark) => mark.type.name === "commentMark");
          if (hasMark) {
            markFrom = pos;
            markTo = pos + node.nodeSize;
          }
        });

        // Create the comment
        if (!projectId || !activeChapter?.id || !userId) return;
        const { id: commentId } = await createComment({
          projectId,
          chapterId: activeChapter.id.toString(),
          content: commentContent,
          stubComment: {
            id: STUB_COMMENT_ID,
            content: commentContent,
            createdAt: new Date().toISOString(),
            author: { id: userId, firstName, lastName },
            parent: null,
            replies: []
          }
        });

        // Replace the mark with temporary id to have a comment id
        const wasEditable = editor.isEditable;
        if (!wasEditable) editor.setEditable(true);
        editor.commands.setTextSelection({ from: markFrom, to: markTo });
        editor.commands.setMark("persistentCommentMark", {
          commentId: commentId
        });
        // Remove temporary comment mark
        removeAllCommentMarks(editor);
        editor.commands.setTextSelection({ from: 0, to: 0 });
        if (!wasEditable) editor.setEditable(false);

        setShowCommentButton(false);
        setShowCommentInput(false);
        // switchToCommentsAndScroll();
        openHandler("comments");
        runTimeout(() => {
          scrollToBottomCallback?.();
        }, 100);
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, activeChapter, projectId, createComment, firstName, lastName]
  );

  const submitInlineComment = () => {
    if (!editor || isSubmitting) return;
    const inputValue = commentInputRef.current?.value ?? "";
    if (!activeChapter?.id) {
      removeAllCommentMarks(editor);
      return;
    }
    handleCommentSubmit(inputValue).then(() => {
      commentsUpdated();
    });
  };

  const hasContent = !!activeChapter?.activeMemo?.mdContent;

  const handleFlagClick = () => {
    if (!editor) return;
    const { selection } = editor.state;
    let quote: string;
    if (selection instanceof CellSelection) {
      const parts: string[] = [];
      selection.forEachCell((cell, pos) => {
        const cellText = editor.state.doc.textBetween(pos + 1, pos + cell.nodeSize - 1, "\n", "\n").trim();
        if (cellText) parts.push(cellText);
      });
      quote = parts.join("\n");
    } else {
      quote = editor.state.doc.textBetween(selection.from, selection.to, "\n");
    }
    if (!quote.trim()) {
      toast.error("Select some text to flag.");
      return;
    }
    setFlagQuote(quote);
    setIsFlagDialogOpen(true);
    setShowCommentButton(false);
  };

  if (!isSynced && !hasContent) {
    return (
      <div className="flex items-center justify-center h-48 text-[14px] text-gray-500 gap-2">
        <Spinner aria-label={t("loading")} className="size-5 text-muted-foreground" />
        Connecting...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-md flex flex-col h-full cursor-text relative">
      {!isSynced && hasContent && (
        <div className="absolute top-2 right-2 text-[12px] leading-[18px] text-gray-400 flex items-center gap-1 z-10 bg-white/80 px-2 py-1 rounded">
          <Spinner aria-label={t("loading")} className="size-3" />
          Syncing...
        </div>
      )}
      {isSynced && isEditorFocused && !isLaptop && (
        <EditorToolbar
          editor={editor}
          fileName={fileName}
          showCommentButton={!editable && showCommentButton && !showCommentInput}
          isReadOnly={!editable}
          onCommentClick={handleCommentClick}
          onChartClick={handleToolbarChartClick}
        />
      )}
      <div ref={scrollContainerRef} className="h-[calc(100%-50px)] overflow-y-scroll relative pt-10">
        {activeChapter?.projectTemplateChapter.isExecutiveSummary && (
          <div className="flex flex-col gap-3 py-3 border-b border-innoveraZinc-150 mb-4">
            <div className="flex items-center justify-between">
              {activeChapter?.lastGeneratedAt && (
                <FigmaTypography variant="body-3" color="zinc500">
                  Last time generated: {formatRelativeTime(activeChapter.lastGeneratedAt)}
                </FigmaTypography>
              )}
              {chatEnabled && <RegenerateButton />}
            </div>
          </div>
        )}

        <ProseWrapper
          className="h-fit overflow-visible"
          editable={editable}
          ref={containerRef}
          onClick={handleContainerClick}
        >
          <EditorContent
            className="focus-visible:outline-hidden focus:[&_.ProseMirror]:outline-hidden [&_.ProseMirror-focused]:outline-hidden text-innoveraZinc-900 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
            editor={editor}
            max={1}
            maxLength={10}
            type="text"
          />
          {showCommentInput && (
            <div
              className="comment-input-container fixed bg-white border border-gray-200 rounded-md shadow-lg p-2"
              style={{
                top: buttonPosition.top - 2,
                left: Math.max(8, Math.min(buttonPosition.left - 2, window.innerWidth - 308)),
                zIndex: 1000,
                minWidth: "200px",
                maxWidth: "300px",
                padding: isSubmitting ? "12px 12px 12px 12px" : undefined
              }}
            >
              <div className="flex items-center gap-2">
                <input
                  ref={commentInputRef}
                  data-testid="inline-comment-input"
                  className="w-full px-2 py-1 text-[14px] border border-gray-300 rounded focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type your comment..."
                  disabled={isSubmitting}
                  onKeyDown={(e) => {
                    if (isEnterSubmit(e, { allowShift: true }) && !isSubmitting) {
                      submitInlineComment();
                    } else if (e.key === "Escape" && !isSubmitting) {
                      setShowCommentInput(false);
                      removeAllCommentMarks(editor);
                    }
                  }}
                />
                {!isSubmitting && (
                  <button
                    type="button"
                    data-testid="inline-comment-submit-button"
                    className="toolbar-comment-button shrink-0 flex items-center justify-center h-7 w-7 rounded text-zinc-900 hover:bg-zinc-100"
                    aria-label="Submit comment"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={submitInlineComment}
                  >
                    <PaperPlaneRightIcon className="h-4 w-4" />
                  </button>
                )}
                {isSubmitting && (
                  <Spinner aria-label={t("loading")} className="size-[18px] shrink-0 text-muted-foreground" />
                )}
              </div>
            </div>
          )}
        </ProseWrapper>
      </div>
      {(!isEditorFocused || isLaptop) && (
        <EditorToolbar
          editor={editor}
          fileName={fileName}
          showCommentButton={!editable && showCommentButton && !showCommentInput}
          isReadOnly={!editable}
          onCommentClick={handleCommentClick}
          onChartClick={handleToolbarChartClick}
        />
      )}
      {showCommentButton && !showCommentInput && !isFlagDialogOpen && (
        <div
          ref={selectionToolbarRef}
          className="selection-floating-toolbar toolbar-comment-button fixed flex items-center gap-1 bg-white border border-innoveraZinc-150 rounded-lg shadow-lg px-1 py-1"
          style={{
            top: Math.max(8, buttonPosition.top - 46),
            left: Math.max(
              (containerRef.current?.getBoundingClientRect().left ?? 0) + 8,
              Math.min(
                buttonPosition.left - 70,
                (containerRef.current?.getBoundingClientRect().right ?? window.innerWidth) - 340
              )
            ),
            zIndex: 1000
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            type="button"
            data-testid="selection-toolbar-comment-button"
            className="toolbar-comment-button flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[14px] font-medium text-zinc-900 hover:bg-zinc-100"
            onClick={handleCommentClick}
          >
            <ChatCircleTextIcon className="h-4 w-4" />
            Comment
          </button>
          <>
          <div className="h-4 w-px bg-zinc-200" />
          <button
            type="button"
            data-testid="selection-toolbar-chart-button"
            className="toolbar-image-ai-button flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[14px] font-medium text-zinc-900 hover:bg-zinc-100"
            onClick={handleImageButtonClick}
          >
            <ChartBarIcon className="h-4 w-4" />
            Chart
            <span className="bg-violet-100 text-[#7c3aed] text-[12px] font-medium leading-none px-1 py-1 rounded">
              AI
            </span>
          </button>
          </>
         <>
              <div className="h-4 w-px bg-zinc-200" />
              <button
                type="button"
                data-testid="selection-toolbar-flag-button"
                className="toolbar-comment-button flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[14px] font-medium text-zinc-900 hover:bg-zinc-100"
                onClick={handleFlagClick}
              >
                <FlagIcon className="h-4 w-4" />
                Flag
              </button>
            </>
        </div>
      )}
      {chapterPromptOpen && (
        <div
          className="selection-image-prompt fixed"
          style={{
            top: Math.max(8, Math.min((chapterPromptAnchor?.top ?? 80) - 2, window.innerHeight - 280)),
            left: Math.max(8, Math.min((chapterPromptAnchor?.left ?? 80) - 2, window.innerWidth - 428)),
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <SelectionImagePrompt
            value={chapterPromptValue}
            onChange={setChapterPromptValue}
            onCancel={handleCancelChapterPrompt}
            onCreate={handleSubmitChapterPrompt}
            error={chapterPromptError}
          />
        </div>
      )}
      {recropModalOpen && recropImage && (
        <RecropModal
          isOpen={recropModalOpen}
          onClose={() => setRecropModalOpen(false)}
          onSuccess={handleRecropSuccess}
          imageUrl={recropImage.imageUrl}
          currentCropCoordinates={recropImage.cropCoordinates}
        />
      )}
      <FlagDeficiencyDialog
        isOpen={isFlagDialogOpen}
        onOpenChange={setIsFlagDialogOpen}
        target={{ kind: "memoChapter", projectId: Number(projectId), chapterId: activeChapter?.id ?? null }}
        quote={flagQuote}
      />
    </div>
  );
}
